/* -----------------Globals--------------- */
import { useMemo, useState } from "react";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
/* -----------------UI--------------- */
import { Image } from "expo-image";
import { List, Text } from "react-native-paper";

import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
/* -----------------Helpers & Hooks--------------- */
import {
	getChapterTextAndSource,
	getShortenedText,
} from "./helpers/parse-metadata";

/* -----------------Types--------------- */
import type { UnifiedCitation } from "@memorang/types/src/session/new-session";

import { isWeb } from "@memorang/helpers/src/common";
import { Platform, type StyleProp, type ViewStyle } from "react-native";
/* -----------------Child components--------------- */
import Box from "../Box";
import MarkdownRenderer from "../MarkdownRenderer";
import { ScrollButtonFlatlist } from "./ScrollButtonFlatlist";

type ReferencesProps = {
	references: UnifiedCitation[] | string;
	handleNavigateToReference?: (url: string) => void;
	onPressReference?: (reference: UnifiedCitation) => void;
	referenceThumbnails?: Record<string, string>;
	withAccordion?: boolean;
	referenceStyle?: StyleProp<ViewStyle>;
	buttonContainerColor?: string;
	buttonIconColor?: string;
	useBottomSheetFlatList?: boolean;
};

type ReferenceAccordionProps = {
	children: React.ReactNode;
	title?: string;
};

const ReferenceAccordion = ({
	children,
	title = "Read more",
}: ReferenceAccordionProps) => {
	const theme = useAppTheme();
	const [isAccordionExpanded, setIsAccordianExpanded] = useState(false);

	const handleOnPressAccordion = () => {
		setIsAccordianExpanded(!isAccordionExpanded);
	};

	return (
		<List.Accordion
			right={() => null}
			style={{
				backgroundColor: theme.colors.secondaryContainer,
				// ? This is a hack to remove the margin vertical inside List.Accordion which cannot be removed with custom style
				marginBottom: isAccordionExpanded ? 0 : -16,
			}}
			title={
				<Box flexDirection="row" alignItems="center" gap={4}>
					<Text variant="labelMedium">{title}</Text>
					<List.Icon
						icon={isAccordionExpanded ? "chevron-up" : "chevron-down"}
					/>
				</Box>
			}
			expanded={isAccordionExpanded}
			onPress={handleOnPressAccordion}
		>
			{children}
		</List.Accordion>
	);
};

const Reference = ({
	reference,
	handleNavigateToReference: _handleNavigateToReference,
	onPressReference,
	referenceThumbnail,
	referenceStyle,
}: {
	reference: UnifiedCitation;
	handleNavigateToReference?: (url: string) => void;
	onPressReference?: (reference: UnifiedCitation) => void;
	referenceThumbnail?: string;
	referenceStyle?: StyleProp<ViewStyle>;
}) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { title: bookName, shortenedReferenceText } =
		getChapterTextAndSource(reference);

	const wordLength = isMobile ? 4 : 6;

	const title = useMemo(() => {
		const title = `${getShortenedText(shortenedReferenceText, wordLength)}`;

		return (
			<Text variant="titleSmall" numberOfLines={1}>
				{title}
			</Text>
		);
	}, [shortenedReferenceText, wordLength]);

	const thumbnail = useMemo(() => {
		const imageUri = reference.thumbnailUri || referenceThumbnail;

		if (!imageUri) {
			return (
				<Icon
					name={reference.url ? "web" : "book-open-page-variant-outline"}
					size={16}
					color={theme.colors.onSurfaceVariant}
				/>
			);
		}

		return (
			<Image
				cachePolicy="memory-disk"
				source={{
					uri: imageUri,
				}}
				style={{
					width: isMobile ? 12 : 12,
					height: isMobile ? 16 : 16,
					alignSelf: "center",
				}}
			/>
		);
	}, [reference, referenceThumbnail, isMobile, theme.colors.onSurfaceVariant]);

	const description = useMemo(() => {
		const description = getShortenedText(bookName);
		return (
			<Box flexDirection="row" alignItems="center" columnGap={8} paddingTop={8}>
				{thumbnail}
				<Text
					variant="labelSmall"
					numberOfLines={1}
					style={{
						color: theme.colors.onSurfaceVariant,
					}}
				>
					Source: {description}
				</Text>
			</Box>
		);
	}, [thumbnail, bookName, theme.colors.onSurfaceVariant]);

	return (
		<List.Item
			title={title}
			description={description}
			onPress={() => {
				onPressReference?.(reference);
				// handleNavigateToReference?.(url);
			}}
			style={[
				{
					paddingVertical: 6,
					backgroundColor: theme.colors.surface,
					borderRadius: 12,
					overflow: "hidden",
					justifyContent: "center",
					maxWidth: 250,
				},
				referenceStyle,
			]}
		/>
	);
};

export const References = ({
	references,
	handleNavigateToReference,
	onPressReference,
	referenceThumbnails,
	withAccordion = true,
	referenceStyle,
	buttonContainerColor,
	buttonIconColor,
	useBottomSheetFlatList = Platform.OS === "android",
}: ReferencesProps) => {
	const theme = useAppTheme();

	const Wrapper = withAccordion ? ReferenceAccordion : Box;

	if (typeof references === "string") {
		return (
			<Wrapper title="Question">
				<Box paddingHorizontal={16}>
					<MarkdownRenderer
						text={references}
						customStyles={{
							text: {
								fontSize: 14,
								lineHeight: 21,
								color: theme.colors.text,
							},
							paragraph: {
								marginBottom: 4,
							},
							image: {
								height: 120,
								width: 120,
								marginTop: 16,
								marginVertical: 16,
							},
						}}
					/>
				</Box>
			</Wrapper>
		);
	}

	return (
		<Wrapper title="Question">
			<ScrollButtonFlatlist<UnifiedCitation>
				useBottomSheetFlatList={useBottomSheetFlatList}
				contentContainerStyle={{
					columnGap: 12,
					paddingRight: isWeb ? 16 : 0,
				}}
				data={references}
				buttonContainerColor={buttonContainerColor}
				buttonIconColor={buttonIconColor}
				keyExtractor={(_item, index) => index.toString()}
				renderItem={({ item }) => {
					return (
						<Reference
							reference={item}
							handleNavigateToReference={handleNavigateToReference}
							onPressReference={onPressReference}
							referenceThumbnail={
								item.thumbnailUri ||
								(item.sourceId &&
									referenceThumbnails?.[item.sourceId.toString()]) ||
								(item.id && referenceThumbnails?.[item.id.toString()]) ||
								undefined
							}
							referenceStyle={referenceStyle}
						/>
					);
				}}
			/>
		</Wrapper>
	);
};
